export const ELIGIBLE_NATIONS = [
  'England',
  'Wales',
  'Scotland',
  'Northern Ireland',
  'Republic of Ireland',
  'Jersey',
  'Guernsey',
  'Sark',
  'Herm',
  'Alderney',
  'Isle of Man',
] as const

export type EligibleNation = (typeof ELIGIBLE_NATIONS)[number]

export type ProfileLayer = 1 | 2 | 3 | 4

export type Jurisdiction =
  | 'england'
  | 'scotland'
  | 'wales'
  | 'northern_ireland'
  | 'jersey'
  | 'guernsey'
  | 'isle_of_man'
  | 'republic_of_ireland'

export type AccessibilityVisibility = 'private' | 'competition_organisers' | 'club_committee'

export type UserRole =
  | 'shooter'
  | 'team_captain'
  | 'competition_admin'
  | 'club_admin'
  | 'range_admin'
  | 'rfd'
  | 'system_admin'

/**
 * Role hierarchy levels (higher number = more privileges).
 * system_admin inherits all lower role permissions.
 * rfd and range_admin are specialist roles at the same level as club_admin.
 */
const ROLE_LEVEL: Record<UserRole, number> = {
  shooter: 0,
  team_captain: 1,
  club_admin: 2,
  competition_admin: 2,
  range_admin: 2,
  rfd: 2,
  system_admin: 3,
}

/**
 * Check whether a user's role meets or exceeds the required role level.
 * system_admin passes every check. Roles at the same level (club_admin,
 * competition_admin, range_admin, rfd) must match exactly unless the user
 * is system_admin.
 */
export function hasRole(userRole: UserRole, requiredRole: UserRole): boolean {
  if (userRole === requiredRole) return true
  // system_admin inherits everything
  if (userRole === 'system_admin') return true
  // For lower-tier checks (e.g. requiredRole is 'shooter'), any higher role passes
  return ROLE_LEVEL[userRole] > ROLE_LEVEL[requiredRole]
}

/**
 * Check whether a user has ANY of the specified roles.
 */
export function hasAnyRole(userRole: UserRole, roles: UserRole[]): boolean {
  return roles.some((r) => hasRole(userRole, r))
}

/** Human-readable labels for each platform role. */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  shooter: 'Shooter',
  team_captain: 'Team Captain',
  competition_admin: 'Competition Admin',
  club_admin: 'Club Admin',
  range_admin: 'Range Admin',
  rfd: 'Registered Firearms Dealer',
  system_admin: 'System Admin',
}

export type SyncStatus = 'SYNCED' | 'PENDING' | 'ERROR'

export interface Profile {
  id: string
  name: string
  email: string
  mobileNumber: string | null
  profilePhoto: string | null
  jurisdiction: Jurisdiction
  zeroId: string
  mfaEnabled: boolean
  dateOfBirth: string | null
  address: string | null
  emergencyContactName: string | null
  emergencyContactNumber: string | null
  workNumber: string | null
  landlineNumber: string | null
  eligibleNations: string[]
  accessibilityConsent: boolean
  accessibilityConsentDate: string | null
  mobilityNeeds: string[]
  sensoryNeeds: string[]
  otherAccessibilityNeeds: string | null
  accessibilityVisibility: AccessibilityVisibility
  facNumber: string | null
  facExpiry: string | null
  sgcNumber: string | null
  sgcExpiry: string | null
  clubs: string[]
  gridNumber: string | null
  tridNumber: string | null
  cpsaNumber: string | null
  nsraNumber: string | null
  ukpsaNumber: string | null
  mlagbNumber: string | null
  issfId: string | null
  bascNumber: string | null
  bictsfNumber: string | null
  sctaNumber: string | null
  wctsaNumber: string | null
  ucpsaNumber: string | null
  nraMembershipNumber: string | null
  roUniqueId: string | null
  pinnedFavourites: PinnedTile[]
  role: UserRole
  syncStatus: SyncStatus
  safetyDisclaimerAcceptedAt: string | null
  privacyPolicyAcceptedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface PinnedTile {
  label: string
  path: string
  order: number
}

export function getProfileLayer(profile: Profile): ProfileLayer {
  if (
    profile.gridNumber ||
    profile.tridNumber ||
    profile.cpsaNumber ||
    profile.nsraNumber ||
    profile.ukpsaNumber ||
    profile.mlagbNumber ||
    profile.clubs.length > 0
  )
    return 4
  if (profile.facNumber || profile.sgcNumber) return 3
  if (profile.dateOfBirth || profile.address) return 2
  return 1
}
